import { create } from 'zustand';
import apiClient from '../service/apiClient';
import { toast } from 'react-toastify';
import { isAxiosError } from 'axios';
import { Page, ResponseError } from '../type/type';
import { NavigateFunction } from 'react-router-dom';

// 가맹점 타입
export interface IFranchise {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  companyNumber: string;
  companyName: string;
  ceo: string;
  telNo: string;
  email: string;
  address: string;
  bizProfileName: string;
  mailOrderNumber?: string | null;
  bizmProfileKey?: string | null;
  deployKey?: string | null;
  googleSheetUrl?: string | null;
  chargeAmount: number;
  customerId: string;
  customerDbId: string;
  role: string;
  contractCount: number;
}

// 가맹점 유저 타입
interface IFranchiseUser {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  loginId: string;
  name: string;
  telNo: string;
  franchiseId: string;
  role: string;
}

// 가맹점 복비 히스토리 타입
interface IFranchiseHistory {
  id: string;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  updatedBy: string;
  amount: number;
  balance: number;
  memo: string;
  chargeType: {
    code: string;
    desc2: string;
    income: boolean;
  };
}

// 가맹점 목록 응답 타입
interface FranchiseResponse extends Page {
  content: IFranchise[];
}

// 본인 가맹점 응답 타입
type MyFranchiseResponse = IFranchise;

// 가맹정 상세 응답 타입
interface FranchiseDetailResponse extends IFranchise {
  customerList: IFranchiseUser[];
  chargeHstList: IFranchiseHistory[];
}

// 가맹점 등록 요청 타입
type PostFranchiseRequest = {
  companyNumber: string;
  companyName: string;
  ceo: string;
  telNo: string;
};

// 가맹점 업데이트 요청 타입
type UpdateFranchiseRequest = {
  companyNumber?: string;
  companyName?: string;
  ceo?: string;
  telNo?: string;
  email?: string;
  bizProfileName?: string;
  mailOrderNumber?: string;
  address?: string;
  newPassword?: string;
};

// Zustand Store Data 타입
type FranchiseData = {
  // 로딩
  getFranchiseLoading: boolean; // 가맹점 목록 로딩
  postFranchiseLoading: boolean; // 가맹점 등록 로딩
  updateFranchiseLoading: boolean; // 가맹점 수정 로딩
  chargeFranchiseLoading: boolean; // 가맹점 복비 충전 로딩

  // 가맹점 목록
  franchises: IFranchise[];
  allFranchises: IFranchise[];
  franchisesPage: Page | null;

  // 본인 가맹점
  myfranchise: IFranchise | null;

  // 가맹점 상세
  franchiseDetail: FranchiseDetailResponse | null;
};

// Zustand Store Action 타입
type FranchiseAction = {
  // 가맹점 목록 가져오기
  getFranchise: (params: {
    companyName?: string;
    companyNumber?: string;
    ceo?: string;
    telNo?: string;
    id?: string;
    page?: number;
    size?: number;
    bizProfileName?: string;
  }) => void;

  // 모든 가맹점 목록 가져오기 (필터용)
  getAllFranchises: () => void;

  // 본인 가맹점 가져오기
  getMyFranchise: () => void;

  // 본인 가맹점 수정
  updateMyFranchise: (franchiseData: UpdateFranchiseRequest) => void;

  // 가맹점 상세 가져오기
  getFranchiseDetail: (id: string) => void;

  // 복비 충전
  chargeFranchise: (
    id: string,
    chargeData: { chargeAmount: number; memo: string }
  ) => void;

  // 가맹점 등록
  postFranchise: (
    franchiseData: PostFranchiseRequest,
    navigate?: NavigateFunction
  ) => Promise<void>;

  // 가맹점 업데이트
  updateFranchise: (id: string, franchiseData: UpdateFranchiseRequest, onSuccess?: () => void) => void;
};

export const useFranchiseStore = create<FranchiseData & FranchiseAction>(
  (set, get) => ({
    getFranchiseLoading: false, // 가맹점 목록 로딩
    postFranchiseLoading: false, // 가맹점 등록 로딩
    updateFranchiseLoading: false, // 가맹점 수정 로딩
    chargeFranchiseLoading: false, // 가맹점 복비 충전 로딩

    // 가맹점 목록
    franchises: [],
    allFranchises: [],
    franchisesPage: null,

    // 본인 가맹점
    myfranchise: null,

    // 가맹점 상세
    franchiseDetail: null,

    // 모든 가맹점 목록 가져오기 (필터용)
    getAllFranchises: async () => {
      if (get().allFranchises.length > 0) {
        return;
      }
      try {
        const response = await apiClient.get<FranchiseResponse>('/franchise', {
          params: {
            size: 1000, // 충분히 큰 숫자로 모든 가맹점을 가져옴
          },
        });
        set({ allFranchises: response.data.content });
      } catch (error) {
        console.error('API 요청 실패:', error);
        toast.error('전체 가맹점 목록을 가져오지 못했습니다.');
      }
    },

    // 가맹점 목록 가져오기
    getFranchise: async ({ companyName, ceo, telNo, id, page, size, companyNumber, bizProfileName }) => {
      set({ getFranchiseLoading: true }); // 가맹점 목록 로딩 시작
      set({ franchiseDetail: null }); // 가맹점 상세 초기화

      try {
        const response = await apiClient.get<FranchiseResponse>('/franchise', {
          params: {
            companyName,
            companyNumber,
            ceo,
            telNo,
            id,
            page,
            size,
            bizProfileName,
          },
        });

        const { content, ...rest } = response.data;

        set({ franchises: content }); // 가맹점 목록 세팅
        set({ franchisesPage: rest }); // 가맹점 페이지 세팅
      } catch (error) {
        console.error('API 요청 실패:', error);
        toast.error('가맹점 목록을 가져오지 못했습니다.');
      }

      set({ getFranchiseLoading: false }); // 가맹점 목록 로딩 끝
    },

    // 본인 가맹점 가져오기
    getMyFranchise: async () => {
      try {
        const response = await apiClient.get<MyFranchiseResponse>(
          '/franchise/who-am-i'
        );

        set({ myfranchise: response.data }); // 본인 가맹점 세팅
      } catch (error) {
        console.error('API 요청 실패:', error);
        toast.error('본인 가맹점 정보를 가져오지 못했습니다.');
      }
    },

    // 본인 가맹점 수정
    updateMyFranchise: async franchiseData => {
      set({ updateFranchiseLoading: true });

      try {
        const response = await apiClient.patch('/franchise', franchiseData);

        if (response.status === 200) {
          toast.success('가맹점 정보가 성공적으로 수정되었습니다!');
          await get().getMyFranchise();
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('본인 가맹점 수정 실패:', error);
          toast.error('가맹점 정보 수정에 실패했습니다.');
        }
      }

      set({ updateFranchiseLoading: false });
    },

    // 가맹점 상세 가져오기
    getFranchiseDetail: async id => {
      try {
        const response = await apiClient.get<FranchiseDetailResponse>(
          `/franchise/${id}`
        );
        set({ franchiseDetail: response.data });
      } catch (error) {
        console.error('API 요청 실패:', error);
        toast.error('가맹점 상세를 가져오지 못했습니다.');
      }
    },

    // 복비 충전
    chargeFranchise: async (id, chargeData) => {
      set({ chargeFranchiseLoading: true }); // 복비 충전 로딩 시작

      try {
        const response = await apiClient.post(
          `/franchise/charge/${id}`,
          chargeData
        );
        if (response.status === 200) {
          toast.success('복비가 충전되었습니다!');

          const currentFranchises = get().franchises;
          const updatedFranchises = currentFranchises.map(franchise => {
            if (franchise.id === id) {
              return {
                ...franchise,
                chargeAmount: franchise.chargeAmount + chargeData.chargeAmount
              };
            }
            return franchise;
          });
          set({ franchises: updatedFranchises });
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('복비 충전 실패:', error);
          toast.error('복비 충전에 실패했습니다.');
        }
      }

      set({ chargeFranchiseLoading: false }); // 주문확정 로딩 시작
    },

    // 가맹점 등록
    postFranchise: async (franchiseData, navigate) => {
      set({ franchises: [] }); // 가맹점 목록 초기화
      set({ postFranchiseLoading: true }); // 가맹점 등록 로딩 시작

      try {
        const response = await apiClient.post('/franchise/join', franchiseData);
        if (response.status === 200) {
          if (navigate) {
            navigate('/franchise');
          }
          toast.success('가맹점이 성공적으로 등록되었습니다!');
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('가맹점 등록 실패:', error);
          toast.error('가맹점 등록에 실패했습니다.');
        }
      }

      set({ postFranchiseLoading: false }); // 가맹점 등록 로딩 끝
    },

    // 가맹점 업데이트
    updateFranchise: async (id, franchiseData, onSuccess) => {
      set({ franchises: [] }); // 가맹점 목록 초기화
      set({ updateFranchiseLoading: true }); // 가맹점 수정 로딩 시작

      try {
        const response = await apiClient.post(
          `/franchise/${id}`,
          franchiseData
        );
        if (response.status === 200) {
          toast.success('가맹점 정보가 성공적으로 수정되었습니다!');
          await Promise.all([get().getFranchiseDetail(id), get().getMyFranchise()]);
          if (onSuccess) {
            onSuccess();
          }
        }
      } catch (error) {
        if (isAxiosError<ResponseError>(error)) {
          toast.error(error.response?.data.errorMsg);
        } else {
          console.error('가맹점 상세 수정 실패:', error);
          toast.error('가맹점 상세 수정에 실패했습니다.');
        }
      }

      set({ updateFranchiseLoading: false }); // 가맹점 수정 로딩 끝
    },
  })
);
